import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import queryString from 'query-string';

import { useSnackbar } from 'contexts/snackbar';
import accountRequests from 'services/account';
import adminRequests from 'services/admin';
import authRequests from 'services/auth';
import courseRequests from 'services/course';
import userRequests from 'services/user';
import ltiRequests from 'services/lti';

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = (props) => {
    const history = useHistory();
    const location = useLocation();
    const { message } = useSnackbar();

    const [auth, setAuth] = useState();
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(false);

    const isLTI = true;

    const getCurrentSession = async () => {
        let cachedUser = null;
        let token = localStorage.getItem('auth_token');
        if (location.pathname === '/lti-launch') {
            const { token: ltiLaunchToken } = queryString.parse(location.search);
            localStorage.removeItem('auth_token');
            if (ltiLaunchToken) {
                localStorage.setItem('auth_token', ltiLaunchToken);
                token = ltiLaunchToken;
                const createUsers = async () => {
                    setLoading(true);
                    const parsedToken = jwt.decode(token);
                    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                    try {
                        await ltiRequests.createLTIUsers({ course_id: parsedToken.course_id });
                    } catch (e) {
                        console.log(e);
                    }
                };
                createUsers();
            }
        }
        if (location.pathname === '/accepted-invitation') {
            const { token: incomingToken } = queryString.parse(location.search);
            localStorage.setItem('auth_token', incomingToken);
            cachedUser = jwt.decode(incomingToken);
            axios.defaults.headers.common.Authorization = `Bearer ${incomingToken}`;
            const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
            setAuth({ ...cachedUser, ...accountData });
            const userData = await userRequests.findUser(cachedUser.user_id);
            setCurrentUser(userData);
        } else if (token) {
            setLoading(true);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            cachedUser = jwt.decode(token);
            setAuth({ ...cachedUser });
            const userData = await userRequests.findUser(cachedUser.user_id);
            setCurrentUser(userData);
            if (cachedUser.owner_account_id) {
                const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                if (!accountData.active) {
                    setLoading(false);
                    return history.push('/lti-incomplete');
                }

                setAuth({ ...cachedUser, ...accountData });
                if (accountData?.course_id) {
                    const existingCourseData = await courseRequests.findCourse(accountData?.course_id);
                    const groupFormation = await courseRequests.listCourseGroupFormations(accountData?.course_id);

                    if (userData && cachedUser?.role && cachedUser.role !== 'admin' && !userData.profile_set_up) {
                        setLoading(false);
                        return history.push('/profile-setup');
                    }

                    if (!accountData.group_id && cachedUser.role === 'student' && !groupFormation) {
                        setLoading(false);
                        return history.push('/lti-incomplete');
                    }

                    if (
                        cachedUser.role === 'instructor' &&
                        !existingCourseData?.finished_course_set_up &&
                        existingCourseData?.created_on > new Date('2021-12-01').getTime()
                    ) {
                        setLoading(false);
                        return history.push('/course-set-up');
                    }
                    if (
                        cachedUser.role === 'instructor' &&
                        !existingCourseData.finished_course_set_up &&
                        groupFormation &&
                        !existingCourseData.finished_group_generation
                    ) {
                        setLoading(false);
                        return history.push('/course-set-up');
                    }

                    if (
                        cachedUser.role === 'student' &&
                        !accountData.group_id &&
                        groupFormation &&
                        !existingCourseData.finished_group_generation
                    ) {
                        setLoading(false);
                        return history.push('/survey');
                    }
                }
            }
            setLoading(false);
        }
    };

    const updateCurrentUser = async (data) => {
        const updateUser = await userRequests.updateUser(auth.user_id, data);
        setCurrentUser(updateUser);
        return updateUser;
    };

    const login = async ({ username = '', password = '' }) => {
        const res = await authRequests.signin({ username, password });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = jwt.decode(token);
        if (cachedUser.owner_account_id) {
            const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
            setAuth({ ...cachedUser, ...accountData });
        } else {
            setAuth({ ...cachedUser });
        }
        setCurrentUser(res);

        if (res.admin === 'true') return 'admin';
        else return res?.profile_set_up;
    };

    const sessionExpired = () => {
        return logout({ isExpired: true });
    };

    const logout = ({ isExpired = false }) => {
        localStorage.removeItem('auth_token');
        axios.defaults.headers.common.Authorization = undefined;
        setAuth(undefined);
        if (isExpired) {
            return history.push('/', { isExpired });
        }
        return history.push('/');
    };

    const selectAccount = async (accountId) => {
        setLoading(true);
        const res = await authRequests.selectAccount({ owner_account_id: accountId });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = jwt.decode(token);
        const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
        const courseData = await courseRequests.findCourse(accountData.course_id);
        setAuth({ ...cachedUser, ...accountData });
        if (
            !courseData?.finished_course_set_up &&
            accountData?.role !== 'student' &&
            courseData?.created_on > new Date('2021-12-01').getTime()
        ) {
            history.push('/course-set-up');
        } else if (accountData?.role === 'instructor') {
            history.push('/group-overview');
        } else if (!accountData?.group_id && accountData?.role === 'student') {
            history.push('/lti-incomplete');
        } else {
            history.push('/dashboard');
        }
        setLoading(false);
    };

    const startCourseSetup = async () => {
        setLoading(true);
        const course = await adminRequests.createCourse({
            school: '',
            name: '',
            faculty: '',
            semester: '',
            section: '',
            finished_course_set_up: false,
        });
        const [{ account }] = await adminRequests.createInstructors({
            course_id: course.id,
            instructors: [{ username: currentUser.username }],
        });

        const res = await authRequests.selectAccount({ owner_account_id: account.id });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = jwt.decode(token);
        const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
        setAuth({ ...cachedUser, ...accountData });

        history.push('/course-set-up');
        setLoading(false);
    };

    const getCurrentUser = () => currentUser || null;

    const updateNotificationPreferences = async (newNotification) => {
        try {
            setAuth((auth) => ({
                ...auth,
                notification: newNotification,
            }));
            await accountRequests.updateAccount(auth.owner_account_id, { notification: newNotification });
        } catch (e) {
            console.error(e);
            message.error('Could not update notification preferences.');
        }
    };

    const updateProfile = async (data) => {
        const prevUser = { ...currentUser };
        const updatedUser = {
            ...currentUser,
            ...data,
        };
        setCurrentUser(updatedUser);

        try {
            await userRequests.updateUser(currentUser.id, data);
            message.success('Profile successfully updated.');
        } catch (e) {
            setCurrentUser(prevUser);
            message.error('Could not update profile.');
        }
    };

    const changeAccount = async () => {
        setAuth((current) => ({ iat: current.iat, user_id: current.user_id, username: current.username }));
        const isLTI = process.env.REACT_APP_AUTH === 'LTI';
        if (isLTI) return history.push('/lti-incomplete');
        const { auth_token } = await authRequests.unselectAccount();
        localStorage.setItem('auth_token', auth_token);
        return history.push('/');
    };

    useEffect(() => {
        if (!auth) getCurrentSession();
    }, [auth, location, getCurrentSession]);

    return (
        <AuthContext.Provider
            value={{
                loading,
                auth,
                currentUser,
                setAuth,
                isLTI,
                getCurrentUser,
                updateCurrentUser,
                updateNotificationPreferences,
                updateProfile,
                login,
                logout,
                selectAccount,
                changeAccount,
                sessionExpired,
                startCourseSetup,
            }}
            {...props}
        />
    );
};

export { useAuth };
export default AuthProvider;
